import {SegmentedControl, ActionList, ActionMenu, CounterLabel, IconButton, TextInput} from '@primer/react'
import {use, type SetStateAction} from 'react'
import {CommentsContext, ViewMode} from './CommentsContext'
import type {AuthorFilterItem} from './utils/get-author-filters'
import {defaultFilterStates, type CommentsFilterState, CommentsSort} from './use-comments-filter-state'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {SearchIcon, CodeReviewIcon, FilterIcon, SortAscIcon, SortDescIcon} from '@primer/octicons-react'

import styles from './CommentsViewControls.module.css'

import {GitHubAvatar} from '@github-ui/github-avatar'
import {FeatureFlags} from '@primer/react/experimental'
import {clsx} from 'clsx'

interface CommentsViewControlsProps {
  /** All authors in the current view mode. */
  authorList: AuthorFilterItem[]
  /** Current filter state, for the current view mode. */
  filterState: CommentsFilterState
  setFilterState: (state: SetStateAction<CommentsFilterState>) => void
  className?: string
}

function isDefaultFilterControls(viewMode: ViewMode, filterState: CommentsFilterState): boolean {
  const defaultState = defaultFilterStates()[viewMode]
  return (
    filterState.hideOutdatedThreads === defaultState.hideOutdatedThreads &&
    filterState.hideResolvedThreads === defaultState.hideResolvedThreads &&
    filterState.selectedAuthor === defaultState.selectedAuthor
  )
}

function FilterInput({filterState, setFilterState}: Pick<CommentsViewControlsProps, 'filterState' | 'setFilterState'>) {
  const updateFilterText = (filterText: string) => setFilterState(prev => ({...prev, filterText}))

  return (
    <TextInput
      block
      aria-label="Filter comments"
      leadingVisual={SearchIcon}
      placeholder="Filter comments"
      value={filterState.filterText}
      className={styles.input}
      onChange={event => updateFilterText(event.target.value)}
    />
  )
}

function FilterMenu({authorList, filterState, setFilterState}: CommentsViewControlsProps) {
  const {viewMode} = use(CommentsContext)

  const toggleResolvedFilter = () => setFilterState(prev => ({...prev, hideResolvedThreads: !prev.hideResolvedThreads}))

  const toggleOutdatedFilter = () => setFilterState(prev => ({...prev, hideOutdatedThreads: !prev.hideOutdatedThreads}))

  const onSelectAuthor = (authorLogin: string) =>
    setFilterState(prev => ({...prev, selectedAuthor: prev.selectedAuthor === authorLogin ? undefined : authorLogin}))

  const isFilteringActive = !isDefaultFilterControls(viewMode, filterState)

  // When the flag is disabled, review thread filters are only rendered in ThreadPreviews mode, so filtering
  // only works in that mode. With it enabled, we show threads in all modes, so filtering works everywhere.
  const hideReviewThreadFilters = isFeatureEnabled('prs_docked_comments_replies')
    ? false
    : viewMode !== ViewMode.ThreadPreviews

  return (
    <div className="position-relative">
      <ActionMenu>
        <ActionMenu.Anchor>
          <IconButton
            icon={FilterIcon}
            aria-label={
              isFilteringActive ? 'Additional comment filters (filters applied)' : 'Additional comment filters'
            }
            description="Additional comment filters"
          />
        </ActionMenu.Anchor>
        <ActionMenu.Overlay width="small">
          <ActionList>
            {!hideReviewThreadFilters && (
              <ActionList.Group selectionVariant="multiple">
                <ActionList.Item selected={!filterState.hideResolvedThreads} onSelect={() => toggleResolvedFilter()}>
                  Show resolved comments
                </ActionList.Item>
                <ActionList.Item selected={!filterState.hideOutdatedThreads} onSelect={() => toggleOutdatedFilter()}>
                  Show outdated comments
                </ActionList.Item>
                <ActionList.Divider />
              </ActionList.Group>
            )}
            <ActionList.Group selectionVariant="single">
              <ActionList.GroupHeading>Filter by author</ActionList.GroupHeading>

              {authorList.map(author => (
                <ActionList.Item
                  key={author.login}
                  selected={filterState.selectedAuthor === author.login}
                  onSelect={() => onSelectAuthor(author.login)}
                >
                  <ActionList.LeadingVisual>
                    <GitHubAvatar src={author.avatarUrl} />
                  </ActionList.LeadingVisual>
                  {author.login}
                  <ActionList.TrailingVisual>
                    <CounterLabel>{author.count}</CounterLabel>
                  </ActionList.TrailingVisual>
                </ActionList.Item>
              ))}
            </ActionList.Group>
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>
      {isFilteringActive && <span className={styles.filterIndicator} aria-hidden="true" />}
    </div>
  )
}

function SortMenu({filterState, setFilterState}: Pick<CommentsViewControlsProps, 'filterState' | 'setFilterState'>) {
  const setSort = (sort: CommentsSort) =>
    setFilterState({
      ...filterState,
      sort,
    })

  return (
    <ActionMenu>
      <ActionMenu.Anchor>
        <IconButton
          // Newest first and code order both share the descending sort icon since we don't have a code sort icon
          icon={filterState.sort === CommentsSort.OldestFirst ? SortAscIcon : SortDescIcon}
          aria-label="Sort comments"
        />
      </ActionMenu.Anchor>

      <ActionMenu.Overlay>
        <ActionList selectionVariant="single">
          <ActionList.Item
            selected={filterState.sort === CommentsSort.NewestFirst}
            onSelect={() => setSort(CommentsSort.NewestFirst)}
          >
            <ActionList.LeadingVisual>
              <SortDescIcon />
            </ActionList.LeadingVisual>
            Newest first
          </ActionList.Item>

          <ActionList.Item
            selected={filterState.sort === CommentsSort.OldestFirst}
            onSelect={() => setSort(CommentsSort.OldestFirst)}
          >
            <ActionList.LeadingVisual>
              <SortAscIcon />
            </ActionList.LeadingVisual>
            Oldest first
          </ActionList.Item>

          <ActionList.Item
            selected={filterState.sort === CommentsSort.DiffOrder}
            onSelect={() => setSort(CommentsSort.DiffOrder)}
          >
            <ActionList.LeadingVisual>
              <CodeReviewIcon />
            </ActionList.LeadingVisual>
            Reviews in code order
          </ActionList.Item>
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

function CommentsViewSwitch() {
  const {viewMode, setViewMode} = use(CommentsContext)

  return isFeatureEnabled('prs_docked_comments_replies') ? (
    <SegmentedControl
      aria-label="Comments view mode"
      onChange={(value: number) => {
        const nextMode = [ViewMode.IssueComments, ViewMode.ThreadPreviews, ViewMode.Mine][value]
        if (nextMode) setViewMode(nextMode)
      }}
    >
      <SegmentedControl.Button selected={viewMode === ViewMode.IssueComments}>All</SegmentedControl.Button>
      <SegmentedControl.Button selected={viewMode === ViewMode.ThreadPreviews}>Unresolved</SegmentedControl.Button>
      <SegmentedControl.Button selected={viewMode === ViewMode.Mine}>Mine</SegmentedControl.Button>
    </SegmentedControl>
  ) : (
    <SegmentedControl
      aria-label="Comments view mode"
      onChange={(value: number) => {
        const nextViewMode = value === 0 ? ViewMode.ThreadPreviews : ViewMode.IssueComments
        setViewMode(nextViewMode)
      }}
    >
      <SegmentedControl.Button selected={viewMode === ViewMode.ThreadPreviews}>Review</SegmentedControl.Button>
      <SegmentedControl.Button selected={viewMode === ViewMode.IssueComments}>Conversation</SegmentedControl.Button>
    </SegmentedControl>
  )
}

/**
 * Renders view controls (filter, sort, etc) for comments.
 */
export function CommentsViewControls(props: CommentsViewControlsProps) {
  return (
    <FeatureFlags flags={{primer_react_css_anchor_positioning: true}}>
      <div className={clsx(styles.container, props.className)}>
        <CommentsViewSwitch />
        {!isFeatureEnabled('prs_docked_comments_replies') && <FilterInput {...props} />}
        <div className={styles.controls}>
          <FilterMenu {...props} />
          {isFeatureEnabled('prs_docked_comments_replies') && <SortMenu {...props} />}
        </div>
      </div>
    </FeatureFlags>
  )
}
